import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/store/cart";
import { Plus, Minus, Play, Mail } from "lucide-react";
import { fileUrl } from "@/components/UploadButton";
import { categoryLabel } from "@/lib/categories";
import RemakeRequestButton from "@/components/RemakeRequestButton";
import { useCampaign } from "@/context/CampaignContext";
import AfterpayMessage from "@/components/AfterpayMessage";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const add = useCart((s) => s.add);
  const { campaign } = useCampaign();
  const holidayActive = campaign.active_holiday && campaign.active_holiday !== "general";

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [slug]);

  const media = useMemo(() => {
    if (!p || p === false) return [];
    const items = [];
    if (p.video) items.push({ type: "video", src: p.video });
    (p.images || []).forEach((src) => items.push({ type: "image", src }));
    return items;
  }, [p]);

  if (p === null) return <div className="max-w-7xl mx-auto px-6 py-32 text-[#757575]">Loading…</div>;
  if (p === false) return (
    <div className="max-w-7xl mx-auto px-6 py-32" data-testid="product-not-found">
      <p className="font-display text-3xl text-[#212121]">Piece not found.</p>
      <Link to="/shop" className="btn-secondary mt-6 inline-flex">Back to shop</Link>
    </div>
  );

  const active = media[activeIdx] || media[0];

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid md:grid-cols-2 gap-12 lg:gap-20">
      <div>
        <div className="aspect-[4/5] overflow-hidden border border-[#B2DFDB] bg-[#FFFFFF] relative">
          {active && active.type === "video" ? (
            <video
              key={active.src}
              src={fileUrl(active.src)}
              controls
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              data-testid="product-video"
            />
          ) : active ? (
            <img src={fileUrl(active.src)} alt={p.name} className={`w-full h-full object-cover ${p.stock < 1 ? "grayscale-[40%]" : ""}`} />
          ) : null}
          {p.stock < 1 && p.category !== "furniture" && (
            <div className="absolute top-4 left-4" data-testid="sold-badge">
              <span className="bg-[#E0F2F1]/90 text-[#B87333] font-bold text-xs uppercase tracking-[0.3em] px-4 py-2 border border-[#B87333]/60 font-medium">
                Sold Out
              </span>
            </div>
          )}
        </div>
        {media.length > 1 && (
          <div className="flex gap-3 mt-4 flex-wrap">
            {media.map((m, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`relative w-20 h-20 border ${activeIdx === i ? "border-[#B87333]" : "border-[#B2DFDB]"} overflow-hidden bg-[#FFFFFF]`}
                data-testid={`thumb-${i}`}
              >
                {m.type === "video" ? (
                  <>
                    <video src={fileUrl(m.src)} muted className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play size={16} className="text-[#212121]" />
                    </span>
                  </>
                ) : (
                  <img src={fileUrl(m.src)} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="smallcaps mb-3">
          {p.subcategory ? `${categoryLabel(p.category)} · ${p.subcategory}` : categoryLabel(p.category)}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#212121] mb-4 leading-tight">{p.name}</h1>
        {(() => {
          const orig = Number(p.price) || 0;
          const onSale = Boolean(p.on_sale && p.sale_price != null && Number(p.sale_price) > 0 && Number(p.sale_price) < orig);
          const shown = onSale ? Number(p.sale_price) : orig;
          return (
            <div className="flex items-baseline gap-4 mb-8" data-testid="price-block">
              {p.is_special && (
                <span className="bg-[#FFF8E1] text-[#B87333] text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 font-black shadow border border-[#B87333]/40 inline-flex items-center gap-1.5" data-testid="special-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  Special Offer
                </span>
              )}
              {onSale && (
                <span className="bg-[#D32F2F] text-white text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 font-semibold" data-testid="sale-badge">
                  Sale
                </span>
              )}
              {p.free_shipping && (
                <span className="bg-[#00BFA5] text-white text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 font-bold shadow inline-flex items-center gap-1.5" data-testid="free-shipping-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  Free Shipping
                </span>
              )}
              {holidayActive && p.perfect_gift && (
                <span className="bg-gradient-to-r from-[#D32F2F] to-[#FFB300] text-white text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 font-semibold" data-testid="gift-badge">
                  🎁 Perfect Gift
                </span>
              )}
              <p className="font-display font-black text-3xl sm:text-4xl text-[#212121]" data-testid="product-price">
                ${shown.toFixed(2)}
              </p>
              {onSale && (
                <p className="font-display font-bold text-xl text-gray-400 line-through" data-testid="product-original-price">
                  ${orig.toFixed(2)}
                </p>
              )}
            </div>
          );
        })()}

        <AfterpayMessage price={(p.on_sale && p.sale_price != null && Number(p.sale_price) < Number(p.price)) ? Number(p.sale_price) : Number(p.price)} className="mb-10" />

        <p className="text-[#757575] leading-relaxed mb-10">{p.description}</p>

        <div className="space-y-3 border-y border-[#B2DFDB] py-6 mb-10">
          {p.sku && (
            <div className="flex justify-between text-sm" data-testid="sku-row">
              <span className="smallcaps">Product code</span>
              <span className="text-[#212121] font-mono text-xs tracking-wider">{p.sku}</span>
            </div>
          )}
          {p.materials && (
            <div className="flex justify-between text-sm">
              <span className="smallcaps">Materials</span>
              <span className="text-[#212121]">{p.materials}</span>
            </div>
          )}
          {p.dimensions && (
            <div className="flex justify-between text-sm">
              <span className="smallcaps">Dimensions</span>
              <span className="text-[#212121]">{p.dimensions}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="smallcaps">Availability</span>
            <span className="text-[#212121]">{p.stock > 0 ? `${p.stock} in stock` : "Sold out"}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-8">
          {p.category === "furniture" ? (
            <button
              onClick={() => nav(`/custom-order?product=${encodeURIComponent(p.slug)}`)}
              className="btn-primary flex-1 justify-center"
              data-testid="furniture-inquire-btn"
            >
              <Mail size={14} /> Inquire about this piece
            </button>
          ) : (
            <>
              <div className="flex items-center border border-[#B2DFDB]">
                <button data-testid="qty-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-[#757575] hover:text-[#212121]" disabled={p.stock < 1}>
                  <Minus size={14} />
                </button>
                <span className="px-4 text-[#212121]" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-increment" onClick={() => setQty(qty + 1)} className="p-3 text-[#757575] hover:text-[#212121]" disabled={p.stock < 1}>
                  <Plus size={14} />
                </button>
              </div>
              {p.stock < 1 ? (
                <RemakeRequestButton product={p} />
              ) : (
                <button
                  onClick={() => add(p, qty)}
                  className="btn-primary flex-1 justify-center"
                  data-testid="add-to-cart-btn"
                >
                  Add to Cart
                </button>
              )}
            </>
          )}
        </div>

        {p.category === "furniture" ? (
          <p className="text-sm text-[#757575]">
            Furniture is built to order and quoted individually. Send us a message and David will reply within 48 hours.
          </p>
        ) : (
          <Link to="/custom-order" className="text-sm text-[#757575] hover:text-[#B87333] font-bold underline underline-offset-4">
            Need something different? Commission a custom piece →
          </Link>
        )}
      </div>
    </div>
  );
}
